/**
 * Features component - Top 3 core features in a clean panel design
 * Similar to Cursor's feature showcase
 */

import {Container} from '../ui/Container';
import {Section} from '../ui/Section';
import {useScrollAnimation} from '../../hooks/useScrollAnimation';

interface Feature {
  title: string;
  description: string;
  detail: string;
}

// Top 3 features to highlight
const topFeatures: Feature[] = [
  {
    title: 'Real-Time Transcription',
    description: 'Accurate transcription of meetings as they happen with speaker identification',
    detail: 'VoxaPilot\'s advanced transcription engine captures every word in real-time, automatically identifying speakers and maintaining context throughout your meetings.',
  },
  {
    title: 'AI Summaries & Insights',
    description: 'Automated meeting summaries, action items, and intelligent insights',
    detail: 'Get instant summaries with key decisions, action items, and insights extracted automatically. Never miss important details from your meetings.',
  },
  {
    title: 'Smart Search & Filters',
    description: 'Find any conversation, keyword, or topic instantly across all meetings',
    detail: 'Powerful search capabilities let you find specific moments, topics, or decisions across your entire meeting history with intelligent filtering.',
  },
];

export function Features() {
  const {ref, isVisible} = useScrollAnimation();

  return (
    <Section id="features" padding="lg" background="white" ref={ref} className="pt-12 bg-background dark:bg-[#1E1E1E]">
      <Container>
        {/* Header Section - Left Aligned */}
        <div className={`text-left mb-8 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <p className="text-sm font-medium text-orange-600 dark:text-orange-500 mb-3 uppercase tracking-wide">
            Features
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-4">
            Turn Every Meeting Into Actionable Intelligence
          </h2>
          <p className="text-lg sm:text-xl text-textSecondary dark:text-[#858585] max-w-2xl">
            Powerful features designed to make every meeting more productive and actionable
          </p>
        </div>

        {/* Top 3 Features - Simplified Panel Design */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {topFeatures.map((feature, index) => (
            <div
              key={feature.title}
              className={`bg-backgroundSecondary dark:bg-[#252526] rounded-2xl p-6 md:p-8 transition-all duration-700 group flex flex-col hover:shadow-xl hover:shadow-gray-200/50 dark:hover:shadow-black/50 hover:-translate-y-1 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{transitionDelay: `${index * 0.15}s`}}
            >
              {/* Feature Header - Left Aligned */}
              <div className="mb-6">
                <h3 className="text-lg md:text-xl font-semibold text-textPrimary dark:text-[#CCCCCC] text-left mb-3">
                  {feature.title}
                </h3>
                <p className="text-base text-textSecondary dark:text-[#858585] leading-relaxed text-left">
                  {feature.description}
                </p>
              </div>

              {/* Visual Placeholder/Image Area */}
              <div className="flex-1 min-h-[200px] bg-white dark:bg-[#1E1E1E] rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center overflow-hidden">
                <div className="w-full h-full p-6 flex flex-col items-center justify-center text-center">
                  <p className="text-sm text-textSecondary dark:text-[#858585]">
                    Feature preview
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Container>
    </Section>
  );
}

